import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { ArrowLeft, MapPin, Calendar, BadgeCheck, Clock, ShieldCheck, AlertTriangle, Info, Mail, Linkedin, FileText, Award, Briefcase, GraduationCap, Lock, Eye, EyeOff, Menu, X, Share2 } from "lucide-react";
import ThemeToggle from "../components/ThemeToggle";
import ShareButtons from "../components/ShareButtons";
import { BookmarkButton } from "../components/BookmarkButton";
import { motion } from "framer-motion";

const formatCurrency = (value) => {
  if (value >= 10000000) {
    return `${(value / 10000000).toFixed(2)} Cr`;
  } else if (value >= 100000) {
    return `${(value / 100000).toFixed(2)} L`;
  }
  return value.toLocaleString('en-IN');
};

const formatStipend = (value) => {
  if (value >= 100000) {
    return `${(value / 100000).toFixed(1)}L/mo`;
  }
  return `${(value / 1000).toFixed(0)}K/mo`;
};

// Verification method display
const getVerificationInfo = (method) => {
  switch (method) {
    case 'college_email':
      return { icon: Mail, label: 'College Email', color: 'text-blue-600' };
    case 'linkedin':
      return { icon: Linkedin, label: 'LinkedIn', color: 'text-sky-600' };
    case 'offer_letter':
      return { icon: FileText, label: 'Offer Letter', color: 'text-green-600' };
    default:
      return null;
  }
};

// PPO Badge
const PPOBadge = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
    <Award className="h-3 w-3" />
    PPO
  </span>
);

const VerificationBadge = ({ status, method }) => {
  if (status === 'verified') {
    const info = getVerificationInfo(method);
    const Icon = info?.icon || BadgeCheck;
    return (
      <div className="flex flex-col items-center gap-1">
        <span className="verification-badge verified">
          <BadgeCheck className="h-3 w-3" />
          Verified
        </span>
        {info && (
          <span className={`text-xs flex items-center gap-1 ${info.color}`}>
            <Icon className="h-3 w-3" />
            {info.label}
          </span>
        )}
      </div>
    );
  }
  return (
    <span className="verification-badge pending">
      <Clock className="h-3 w-3" />
      Pending
    </span>
  );
};

// Calculate confidence score based on number of reports
const getConfidenceScore = (placementCount) => {
  if (placementCount >= 50) return 95;
  if (placementCount >= 30) return 85;
  if (placementCount >= 15) return 70;
  if (placementCount >= 10) return 60;
  if (placementCount >= 5) return 45;
  return 30;
};

const ConfidenceBadge = ({ score, count }) => {
  let level, icon, label;
  
  if (score >= 70) {
    level = "high";
    icon = <ShieldCheck className="h-3 w-3" />;
    label = "High Confidence";
  } else if (score >= 50) {
    level = "medium";
    icon = <Info className="h-3 w-3" />;
    label = "Medium Confidence";
  } else {
    level = "low";
    icon = <AlertTriangle className="h-3 w-3" />;
    label = "Low Confidence";
  }
  
  return (
    <div className={`confidence-badge confidence-${level}`} title={`${label} - Based on ${count} verified reports`}>
      {icon}
      <span>{score}%</span>
    </div>
  );
};

const SalaryBreakdownBar = ({ breakdown, total }) => {
  const getWidth = (value) => {
    if (!total || total === 0 || !value) return 0;
    return (value / total) * 100;
  };

  const fixedWidth = getWidth(breakdown.fixed);
  const variableWidth = getWidth(breakdown.variable);
  const esopsWidth = getWidth(breakdown.esops);
  const bonusWidth = getWidth(breakdown.joining_bonus);
  const relocationWidth = getWidth(breakdown.relocation);

  return (
    <div className="space-y-2">
      <div className="salary-bar">
        {fixedWidth > 0 && (
          <div className="fixed-salary" style={{ width: `${fixedWidth}%` }} title={`Fixed: ${formatCurrency(breakdown.fixed)}`} />
        )}
        {variableWidth > 0 && (
          <div className="variable-salary" style={{ width: `${variableWidth}%` }} title={`Variable: ${formatCurrency(breakdown.variable)}`} />
        )}
        {esopsWidth > 0 && (
          <div className="esops-salary" style={{ width: `${esopsWidth}%` }} title={`ESOPs: ${formatCurrency(breakdown.esops)}`} />
        )}
        {bonusWidth > 0 && (
          <div className="bonus-salary" style={{ width: `${bonusWidth}%` }} title={`Bonus: ${formatCurrency(breakdown.joining_bonus)}`} />
        )}
        {relocationWidth > 0 && (
          <div className="relocation-salary" style={{ width: `${relocationWidth}%` }} title={`Relocation: ${formatCurrency(breakdown.relocation)}`} />
        )}
      </div>
      <div className="flex flex-wrap gap-3 text-xs">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: '#3b82f6' }} /> Fixed
        </span>
        {breakdown.variable > 0 && (
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: '#10b981' }} /> Variable
          </span>
        )}
        {breakdown.esops > 0 && (
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: '#f59e0b' }} /> ESOPs
          </span>
        )}
        {breakdown.joining_bonus > 0 && (
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: '#8b5cf6' }} /> Bonus
          </span>
        )}
        {breakdown.relocation > 0 && (
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: '#6b7280' }} /> Relocation
          </span>
        )}
      </div>
    </div>
  );
};

// Gated content overlay
const GatedOverlay = ({ onSubmit, onLogin, isLoggedIn }) => (
  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/95 to-transparent flex flex-col items-center justify-end pb-8 z-10">
    <div className="text-center max-w-md px-4">
      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <Lock className="h-8 w-8 text-primary" />
      </div>
      <h3 className="font-heading text-xl font-bold uppercase mb-2">
        Unlock Full Data
      </h3>
      <p className="text-muted-foreground text-sm mb-6">
        Submit your own placement or internship data to access complete salary reports from this college.
      </p>
      <Button 
        onClick={isLoggedIn ? onSubmit : onLogin}
        className="h-12 px-8 font-medium"
        data-testid="unlock-data-btn"
      >
        {isLoggedIn ? "Submit Your Data to Unlock" : "Sign In to Submit & Unlock"}
      </Button>
    </div>
  </div>
);

const FREE_PREVIEW_COUNT = 5; // Number of verified placements to show for free

const CollegeDetail = () => {
  const { collegeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  
  const [collegeData, setCollegeData] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [placements, setPlacements] = useState([]);

  const handleMobileNav = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };
  
  const [userPlacements, setUserPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState(searchParams.get('program') || "all");
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedType, setSelectedType] = useState("placement");
  const [showPending, setShowPending] = useState(false);

  // Check if user has submitted any placements
  const hasSubmitted = userPlacements.length > 0;
  const hasFullAccess = hasSubmitted;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const requests = [
          axios.get(`${API}/colleges/${collegeId}`),
          axios.get(`${API}/placements`, { params: { college_id: collegeId, limit: 500 } })
        ];
        
        // If user is logged in, also fetch their placements
        if (user) {
          requests.push(axios.get(`${API}/my-placements`));
        }
        
        const responses = await Promise.all(requests);
        setCollegeData(responses[0].data);
        setPlacements(responses[1].data);
        
        if (responses[2]) {
          setUserPlacements(responses[2].data);
        }
        
        const urlProgram = searchParams.get('program');
        if (urlProgram && responses[0].data.programs.some(p => p.program_id === urlProgram)) {
          setSelectedProgram(urlProgram);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [collegeId, searchParams, user]);

  // Separate verified and pending placements
  const { verifiedPlacements, pendingPlacements } = useMemo(() => {
    const filtered = placements.filter(p => {
      if (selectedProgram && selectedProgram !== "all" && p.program_id !== selectedProgram) return false;
      if (selectedYear && selectedYear !== "all" && p.year !== parseInt(selectedYear)) return false;
      if (selectedType !== "all") {
        const pType = p.placement_type || "placement";
        if (pType !== selectedType) return false;
      }
      return true;
    });
    
    return {
      verifiedPlacements: filtered.filter(p => p.verification_status === 'verified'),
      pendingPlacements: filtered.filter(p => p.verification_status !== 'verified')
    };
  }, [placements, selectedProgram, selectedYear, selectedType]);

  const years = [...new Set(placements.map(p => p.year))].sort((a, b) => b - a);
  
  // Count placements and internships (only verified)
  const verifiedOnly = placements.filter(p => p.verification_status === 'verified');
  const placementCount = verifiedOnly.filter(p => (p.placement_type || "placement") === "placement").length;
  const internshipCount = verifiedOnly.filter(p => p.placement_type === "internship").length;
  const ppoCount = verifiedOnly.filter(p => p.is_ppo).length;
  const pendingCount = placements.filter(p => p.verification_status !== 'verified').length;

  // Calculate stats ONLY from verified placements
  const currentStats = useMemo(() => {
    if (verifiedPlacements.length === 0) return null;
    
    if (selectedType === "internship") {
      const stipends = verifiedPlacements.filter(p => p.internship_stipend).map(p => p.internship_stipend);
      if (stipends.length === 0) return null;
      const avgStipend = stipends.reduce((a, b) => a + b, 0) / stipends.length;
      const maxStipend = Math.max(...stipends);
      return {
        total: verifiedPlacements.length,
        avgCtc: avgStipend,
        maxCtc: maxStipend,
        isInternship: true,
        confidence: getConfidenceScore(verifiedPlacements.length)
      };
    }
    
    const ctcs = verifiedPlacements.map(p => p.total_ctc);
    const avgCtc = ctcs.reduce((a, b) => a + b, 0) / ctcs.length;
    const maxCtc = Math.max(...ctcs);
    
    return {
      total: verifiedPlacements.length,
      avgCtc,
      maxCtc,
      confidence: getConfidenceScore(verifiedPlacements.length)
    };
  }, [verifiedPlacements, selectedType]);

  // Placements to display (limited if no access)
  const displayedVerifiedPlacements = hasFullAccess 
    ? verifiedPlacements 
    : verifiedPlacements.slice(0, FREE_PREVIEW_COUNT);
  
  const hasHiddenPlacements = !hasFullAccess && verifiedPlacements.length > FREE_PREVIEW_COUNT;
  const hiddenCount = verifiedPlacements.length - FREE_PREVIEW_COUNT;

  if (loading) {
    return (
      <div className="min-h-screen">
        <nav className="border-b border-border h-16" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <div className="skeleton h-8 w-32 mb-8" />
          <div className="skeleton h-48 w-full mb-8" />
          <div className="skeleton h-96 w-full" />
        </div>
      </div>
    );
  }

  if (!collegeData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold">College Not Found</h2>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Back to Rankings
          </Button>
        </div>
      </div>
    );
  }

  const { college, programs, stats } = collegeData;

  const getSelectedProgramName = () => {
    if (selectedProgram === "all") return null;
    const prog = programs.find(p => p.program_id === selectedProgram);
    return prog?.short_name || prog?.name;
  };

  // Render table row
  const renderPlacementRow = (placement, index, isBlurred = false) => (
    <tr 
      key={placement.placement_id} 
      className={`border-t border-border table-row-hover stagger-row ${isBlurred ? 'blur-sm select-none' : ''}`}
      data-testid={`placement-row-${index}`}
    >
      <td className="p-4">
        <div className="flex items-center gap-2">
          <span className="font-medium">{placement.company}</span>
          {placement.is_ppo && <PPOBadge />}
        </div>
        <div className="text-sm text-muted-foreground">{placement.role}</div>
      </td>
      <td className="p-4 hidden md:table-cell">
        <span className="text-sm">{placement.program_name}</span>
      </td>
      {selectedType === "internship" ? (
        <>
          <td className="p-4 text-right">
            <span className="font-mono font-bold text-lg">
              {placement.internship_stipend ? formatStipend(placement.internship_stipend) : '-'}
            </span>
          </td>
          <td className="p-4 text-center hidden sm:table-cell">
            <span className="font-mono">{placement.internship_duration || '-'} mo</span>
          </td>
        </>
      ) : (
        <>
          <td className="p-4 hidden lg:table-cell">
            <SalaryBreakdownBar 
              breakdown={placement.salary_breakdown} 
              total={placement.total_ctc}
            />
          </td>
          <td className="p-4 text-right">
            <span className="font-mono font-bold text-lg">{formatCurrency(placement.total_ctc)}</span>
          </td>
        </>
      )}
      <td className="p-4 text-center hidden sm:table-cell">
        <span className="font-mono">{placement.year}</span>
      </td>
      <td className="p-4 text-center hidden sm:table-cell">
        <VerificationBadge 
          status={placement.verification_status} 
          method={placement.verification_method} 
        />
      </td>
    </tr>
  );

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="border-b border-border bg-background sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="font-heading text-lg sm:text-xl font-semibold tracking-tight" data-testid="logo">
              Transplacement
            </Link>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-4">
              <Link to="/dashboard" className="text-sm font-medium hover:text-primary/80" data-testid="nav-rankings">
                Rankings
              </Link>
              {user ? (
                <>
                  <Link to="/submit" className="text-sm font-medium hover:text-primary/80" data-testid="nav-submit">
                    Submit
                  </Link>
                  <Link to="/profile" data-testid="nav-profile">
                    <Button variant="outline" className="h-10 px-4 text-sm font-medium rounded-full">
                      Profile
                    </Button>
                  </Link>
                </>
              ) : (
                <Button onClick={() => login('/college')} className="h-10 px-6 text-sm font-medium rounded-full" data-testid="nav-login-btn">
                  Sign In
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 -mr-2 touch-target"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-background z-40 md:hidden">
          <div className="flex flex-col p-6 pt-20">
            <button 
              onClick={() => handleMobileNav('/dashboard')}
              className="py-4 text-lg font-medium border-b border-border text-left"
            >
              Rankings
            </button>
            {user ? (
              <>
                <button 
                  onClick={() => handleMobileNav('/submit')}
                  className="py-4 text-lg font-medium border-b border-border text-left"
                >
                  Submit Placement
                </button>
                <button 
                  onClick={() => handleMobileNav('/profile')}
                  className="py-4 text-lg font-medium border-b border-border text-left"
                >
                  My Profile
                </button>
              </>
            ) : (
              <button 
                onClick={() => { setMobileMenuOpen(false); login('/college'); }}
                className="mt-6 h-12 bg-primary text-primary-foreground font-medium font-medium text-sm"
              >
                Sign In with Google
              </button>
            )}
          </div>
        </div>
      )}

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-3 sm:py-4">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Rankings
        </button>
      </div>

      {/* College Header */}
      <section className="border-b border-border py-6 sm:py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex flex-col md:flex-row gap-6 sm:gap-8 items-start">
            <div className="flex-1">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                <span className="px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-medium bg-secondary border border-border font-medium">
                  {college.type}
                </span>
                {hasFullAccess && (
                  <span className="px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-medium bg-green-50 text-green-700 border border-green-200 flex items-center gap-1">
                    <Eye className="h-3 w-3" />
                    Full Access
                  </span>
                )}
              </div>
              <div className="flex items-start justify-between gap-4">
                <h1 className="font-heading text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight" data-testid="college-name">
                  {college.name}
                  {getSelectedProgramName() && (
                    <span className="text-muted-foreground font-normal text-base sm:text-lg md:text-2xl ml-2 sm:ml-3 block sm:inline mt-1 sm:mt-0">
                      — {getSelectedProgramName()}
                    </span>
                  )}
                </h1>
                {/* Share & Bookmark buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <BookmarkButton college={{ college_id: collegeId, name: college.name }} />
                  <div className="hidden sm:block">
                    <ShareButtons 
                      title={`${college.name} Placement Data - Transplacement`}
                      description={`Check out verified placement data for ${college.name}`}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-3 sm:mt-4 text-muted-foreground text-sm">
                <span className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  {college.location}
                </span>
                <span className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Est. {college.established}
                </span>
              </div>
              
              {/* Mobile share buttons */}
              <div className="sm:hidden mt-4">
                <ShareButtons 
                  title={`${college.name} Placement Data - Transplacement`}
                  description={`Check out verified placement data for ${college.name}`}
                />
              </div>
              
              {/* Programs */}
              <div className="filter-scroll sm:flex sm:flex-wrap sm:gap-2 sm:overflow-visible mt-4">
                <button
                  onClick={() => setSelectedProgram("all")}
                  className={`filter-chip flex-shrink-0 ${
                    selectedProgram === "all" 
                      ? 'active' 
                      : ''
                  }`}
                  data-testid="program-all"
                >
                  All Programs
                </button>
                {programs.map(prog => (
                  <button 
                    key={prog.program_id}
                    onClick={() => setSelectedProgram(prog.program_id)}
                    className={`filter-chip flex-shrink-0 ${
                      selectedProgram === prog.program_id 
                        ? 'active' 
                        : ''
                    }`}
                    data-testid={`program-${prog.program_id}`}
                  >
                    {prog.short_name}
                  </button>
                ))}
              </div>
            </div>

            {/* Stats - Only from verified placements */}
            <div className="grid grid-cols-2 gap-4 w-full md:w-auto">
              <div className="card-data" data-testid="stat-total">
                <div className="font-mono text-2xl font-bold">{currentStats?.total || 0}</div>
                <div className="text-xs text-muted-foreground font-medium mt-1">
                  Verified Reports
                </div>
              </div>
              <div className="card-data" data-testid="stat-avg">
                <div className="font-mono text-2xl font-bold">
                  {currentStats?.isInternship 
                    ? formatStipend(currentStats?.avgCtc || 0)
                    : formatCurrency(currentStats?.avgCtc || 0)
                  }
                </div>
                <div className="text-xs text-muted-foreground font-medium mt-1">
                  {currentStats?.isInternship ? 'Avg Stipend' : 'Avg CTC'}
                </div>
              </div>
              <div className="card-data" data-testid="stat-max">
                <div className="font-mono text-2xl font-bold">
                  {currentStats?.isInternship 
                    ? formatStipend(currentStats?.maxCtc || 0)
                    : formatCurrency(currentStats?.maxCtc || 0)
                  }
                </div>
                <div className="text-xs text-muted-foreground font-medium mt-1">
                  {currentStats?.isInternship ? 'Max Stipend' : 'Max CTC'}
                </div>
              </div>
              <div className="card-data" data-testid="stat-confidence">
                <div className="flex items-center gap-2">
                  <ConfidenceBadge 
                    score={currentStats?.confidence || 0} 
                    count={currentStats?.total || 0} 
                  />
                </div>
                <div className="text-xs text-muted-foreground font-medium mt-1">Confidence</div>
              </div>
            </div>
          </div>

          {/* Top Companies */}
          {stats.top_companies && stats.top_companies.length > 0 && (
            <div className="mt-8 pt-6 border-t border-border">
              <div className="text-xs text-muted-foreground font-medium mb-3">Top Recruiters</div>
              <div className="flex flex-wrap gap-2">
                {stats.top_companies.map((company, i) => (
                  <span key={i} className="px-4 py-2 bg-secondary border border-border text-sm font-medium">
                    {company}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Type & Year Filter */}
      <section className="border-b border-border py-4 bg-background sticky top-16 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Type Filter */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Type:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedType("placement")}
                  className={`filter-chip text-sm flex items-center gap-2 ${selectedType === "placement" ? 'active' : ''}`}
                  data-testid="type-placement"
                >
                  <Briefcase className="h-3 w-3" />
                  Placements ({placementCount})
                </button>
                <button
                  onClick={() => setSelectedType("internship")}
                  className={`filter-chip text-sm flex items-center gap-2 ${selectedType === "internship" ? 'active' : ''}`}
                  data-testid="type-internship"
                >
                  <GraduationCap className="h-3 w-3" />
                  Internships ({internshipCount})
                </button>
              </div>
              {ppoCount > 0 && selectedType === "placement" && (
                <span className="text-xs text-purple-600 flex items-center gap-1">
                  <Award className="h-3 w-3" />
                  {ppoCount} PPO
                </span>
              )}
            </div>

            {/* Year Filter */}
            <div className="flex items-center gap-3 md:ml-auto">
              <span className="text-sm text-muted-foreground">Year:</span>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedYear("all")}
                  className={`filter-chip text-sm ${selectedYear === "all" ? 'active' : ''}`}
                  data-testid="year-all"
                >
                  All
                </button>
                {years.map(year => (
                  <button
                    key={year}
                    onClick={() => setSelectedYear(year.toString())}
                    className={`filter-chip text-sm ${selectedYear === year.toString() ? 'active' : ''}`}
                    data-testid={`year-${year}`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
            <span>{verifiedPlacements.length} verified {selectedType === "internship" ? "internships" : "placements"}</span>
            {pendingPlacements.length > 0 && (
              <span className="text-amber-600">+ {pendingPlacements.length} pending verification</span>
            )}
          </div>
        </div>
      </section>

      {/* Verified Placements Table */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold uppercase flex items-center gap-2">
              <BadgeCheck className="h-5 w-5 text-green-600" />
              Verified {selectedType === "internship" ? "Internships" : "Placements"}
            </h2>
            {!hasFullAccess && verifiedPlacements.length > FREE_PREVIEW_COUNT && (
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <EyeOff className="h-4 w-4" />
                {hiddenCount} more hidden
              </span>
            )}
          </div>

          <div className="relative">
            <div className="table-wrapper border border-border">
              <table className="w-full">
                <thead className="bg-secondary">
                  <tr>
                    <th className="text-left font-heading uppercase text-xs tracking-wider text-muted-foreground p-4">
                      Company & Role
                    </th>
                    <th className="text-left font-heading uppercase text-xs tracking-wider text-muted-foreground p-4 hidden md:table-cell">
                      Program
                    </th>
                    {selectedType === "internship" ? (
                      <>
                        <th className="text-right font-heading uppercase text-xs tracking-wider text-muted-foreground p-4">
                          Stipend
                        </th>
                        <th className="text-center font-heading uppercase text-xs tracking-wider text-muted-foreground p-4 hidden sm:table-cell">
                          Duration
                        </th>
                      </>
                    ) : (
                      <>
                        <th className="text-left font-heading uppercase text-xs tracking-wider text-muted-foreground p-4 hidden lg:table-cell w-64">
                          Salary Breakdown
                        </th>
                        <th className="text-right font-heading uppercase text-xs tracking-wider text-muted-foreground p-4">
                          Total CTC
                        </th>
                      </>
                    )}
                    <th className="text-center font-heading uppercase text-xs tracking-wider text-muted-foreground p-4 hidden sm:table-cell w-20">
                      Year
                    </th>
                    <th className="text-center font-heading uppercase text-xs tracking-wider text-muted-foreground p-4 hidden sm:table-cell w-24">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {verifiedPlacements.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-muted-foreground">
                        No verified {selectedType === "internship" ? "internships" : "placements"} found.
                      </td>
                    </tr>
                  ) : (
                    <>
                      {displayedVerifiedPlacements.map((placement, index) => 
                        renderPlacementRow(placement, index)
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {/* Gated overlay for hidden content */}
            {hasHiddenPlacements && (
              <div className="mt-4 border border-dashed border-border p-8 text-center bg-secondary relative">
                <Lock className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                <h3 className="font-heading text-lg font-bold uppercase mb-2">
                  {hiddenCount} More Verified {selectedType === "internship" ? "Internships" : "Placements"}
                </h3>
                <p className="text-muted-foreground text-sm mb-4 max-w-md mx-auto">
                  Submit your own placement or internship data to unlock full access to all salary reports.
                </p>
                <Button 
                  onClick={() => user ? navigate('/submit') : login()}
                  className="h-12 px-8 font-medium"
                  data-testid="unlock-btn"
                >
                  {user ? "Submit to Unlock" : "Sign In & Submit to Unlock"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Pending Placements Table (Separate) */}
      {pendingPlacements.length > 0 && (
        <section className="py-8 border-t border-border bg-amber-50/30">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold uppercase flex items-center gap-2 text-amber-700">
                <Clock className="h-5 w-5" />
                Pending Verification ({pendingPlacements.length})
              </h2>
              <button
                onClick={() => setShowPending(!showPending)}
                className="text-sm text-amber-700 hover:text-amber-900 flex items-center gap-1"
                data-testid="toggle-pending"
              >
                {showPending ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                {showPending ? 'Hide' : 'Show'}
              </button>
            </div>

            <p className="text-sm text-amber-700/80 mb-4">
              These reports are awaiting verification and are not included in the average calculations above.
            </p>

            {showPending && (
              <div className="table-wrapper border border-amber-200 bg-white">
                <table className="w-full">
                  <thead className="bg-amber-50">
                    <tr>
                      <th className="text-left font-heading uppercase text-xs tracking-wider text-amber-700 p-4">
                        Company & Role
                      </th>
                      <th className="text-left font-heading uppercase text-xs tracking-wider text-amber-700 p-4 hidden md:table-cell">
                        Program
                      </th>
                      <th className="text-right font-heading uppercase text-xs tracking-wider text-amber-700 p-4">
                        {selectedType === "internship" ? "Stipend" : "Total CTC"}
                      </th>
                      <th className="text-center font-heading uppercase text-xs tracking-wider text-amber-700 p-4 hidden sm:table-cell w-20">
                        Year
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingPlacements.map((placement, index) => (
                      <tr 
                        key={placement.placement_id} 
                        className="border-t border-amber-100 hover:bg-amber-50/50"
                        data-testid={`pending-row-${index}`}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{placement.company}</span>
                            {placement.is_ppo && <PPOBadge />}
                          </div>
                          <div className="text-sm text-muted-foreground">{placement.role}</div>
                        </td>
                        <td className="p-4 hidden md:table-cell">
                          <span className="text-sm">{placement.program_name}</span>
                        </td>
                        <td className="p-4 text-right">
                          <span className="font-mono font-bold">
                            {selectedType === "internship" && placement.internship_stipend
                              ? formatStipend(placement.internship_stipend)
                              : formatCurrency(placement.total_ctc)
                            }
                          </span>
                        </td>
                        <td className="p-4 text-center hidden sm:table-cell">
                          <span className="font-mono">{placement.year}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-12 border-t border-border bg-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-xl font-bold uppercase tracking-tight">
            {hasFullAccess ? "Help Others Too!" : `Are you from ${college.name}?`}
          </h2>
          <p className="text-muted-foreground mt-2">
            {hasFullAccess 
              ? "Your submission helped unlock this data. Keep contributing to help more students!"
              : "Submit your placement or internship data to unlock full access to all reports."
            }
          </p>
          <Button 
            onClick={() => user ? navigate('/submit') : login()}
            className="mt-6 h-12 px-8 font-medium"
            data-testid="submit-placement-btn"
          >
            Submit Your Data
          </Button>
        </div>
      </section>
    </div>
  );
};

export default CollegeDetail;
